"use client";

import { useCallback, useState } from "react";

import { z } from "zod";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CONTACT_RECAPTCHA_ACTION } from "@/features/best/contact-us/recaptcha-constants";
import { useRecaptcha } from "@/features/best/contact-us/recaptcha-provider";
import { withBasePath } from "@/lib/base-path";
import { cn } from "@/lib/utils";

type ContactType = "partnership" | "customer-support";

const baseSchema = z.object({
  name: z.string().min(1, "Name is required"),
  email: z.email("Invalid email address"),
  message: z.string(),
});

const partnershipSchema = baseSchema.extend({
  company: z.string().min(1, "Company is required"),
  website: z.url("Invalid URL"),
});

type SubmitStatus =
  | { status: "idle" }
  | { status: "submitting" }
  | { status: "success" }
  | { status: "error"; message: string };

const PARTNERSHIP_DESCRIPTION =
  "Looking to grow sales? Get in touch with our Partnerships team using the form and learn more about what Top10-USA.com can do for your brand.";

const CUSTOMER_SUPPORT_DESCRIPTION =
  "Have a question or need help? Our Customer Support team is here to assist you. Fill out the form below and we'll get back to you as soon as possible.";

const GENERIC_ERROR = "Something went wrong. Please try again.";

export function ContactForm() {
  const recaptcha = useRecaptcha();
  const [contactType, setContactType] = useState<ContactType>("partnership");
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>({
    status: "idle",
  });

  const handleSubmit = useCallback(
    async (e: React.SubmitEvent<HTMLFormElement>): Promise<void> => {
      e.preventDefault();

      const formElement = e.currentTarget;

      setSubmitStatus({ status: "submitting" });

      let captchaToken = "";
      if (recaptcha) {
        try {
          captchaToken = await recaptcha.executeRecaptcha(
            CONTACT_RECAPTCHA_ACTION,
          );
        } catch (error) {
          console.error("contact form: captcha execute failed", error);
          setSubmitStatus({ status: "error", message: GENERIC_ERROR });
          return;
        }

        if (!captchaToken) {
          console.error("contact form: empty captcha token");
          setSubmitStatus({ status: "error", message: GENERIC_ERROR });
          return;
        }
      }

      const formData = new FormData(formElement);
      const raw = Object.fromEntries(formData.entries());
      const schema =
        contactType === "partnership" ? partnershipSchema : baseSchema;
      const parsed = schema.safeParse(raw);

      if (!parsed.success) {
        const firstMessage = parsed.error.issues[0]?.message ?? "Invalid input";
        setSubmitStatus({ status: "error", message: firstMessage });
        return;
      }

      const payload = {
        contactType,
        ...parsed.data,
        ...(captchaToken ? { "g-recaptcha-response": captchaToken } : {}),
      };

      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10_000);

        const res = await fetch(withBasePath("/api/contact/send"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        const data = (await res.json()) as { status: string; message: string };

        if (data.status === "success") {
          formElement.reset();
          setSubmitStatus({ status: "success" });
        } else {
          console.error("contact form: api error", {
            status: res.status,
            message: data.message,
          });
          setSubmitStatus({ status: "error", message: GENERIC_ERROR });
        }
      } catch (error) {
        console.error("contact form: request failed", error);
        setSubmitStatus({ status: "error", message: GENERIC_ERROR });
      }
    },
    [recaptcha, contactType],
  );

  if (submitStatus.status === "success") {
    return (
      <div className="rounded-xl border border-green-200 bg-green-50 p-4">
        <p className="body2 text-green-800">
          Thank you for your message! We&apos;ll get back to you soon.
        </p>
      </div>
    );
  }

  const isPartnership = contactType === "partnership";
  const isSubmitting = submitStatus.status === "submitting";

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col gap-6"
    >
      {/* Toggle */}
      <div className="flex gap-1 items-center p-1 bg-surface-grayscale-lighter border border-border-grayscale-default rounded-2xl">
        <button
          type="button"
          onClick={() => setContactType("partnership")}
          className={cn(
            "flex-1 py-2 text-center body2 rounded-xl transition-all",
            isPartnership
              ? "bg-surface-grayscale-subtle shadow text-text-grayscale-caption"
              : "text-text-grayscale-body",
          )}
        >
          Partnership
        </button>
        <button
          type="button"
          onClick={() => setContactType("customer-support")}
          className={cn(
            "flex-1 py-2 text-center body2 rounded-xl transition-all",
            !isPartnership
              ? "bg-surface-grayscale-subtle shadow text-text-grayscale-caption"
              : "text-text-grayscale-body",
          )}
        >
          Customer support
        </button>
      </div>

      {/* Description */}
      <p className="body2 text-[#727d87]">
        {isPartnership ? PARTNERSHIP_DESCRIPTION : CUSTOMER_SUPPORT_DESCRIPTION}
      </p>

      {/* Business information (partnership only) */}
      {isPartnership && (
        <div className="flex flex-col gap-2">
          <p className="h4 text-text-grayscale-subtitle">
            Business information
          </p>
          <div className="flex flex-col gap-3 p-3 bg-surface-grayscale-subtle rounded-xl shadow-[-2px_-2px_9.5px_rgba(0,0,0,0.04),4px_4px_9.5px_rgba(0,0,0,0.06)]">
            <div className="flex flex-col gap-1">
              <label
                htmlFor="company"
                className="mini2 text-text-grayscale-caption"
              >
                Company*
              </label>
              <Input
                id="company"
                name="company"
                placeholder="Enter your company name"
                required
                className="border-border-grayscale-default rounded-lg px-3 py-2 body1 placeholder:text-text-grayscale-disabled shadow-none h-auto"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label
                htmlFor="website"
                className="mini2 text-text-grayscale-caption"
              >
                Link to Amazon store or website*
              </label>
              <Input
                id="website"
                name="website"
                type="url"
                placeholder="http://www.example.com"
                required
                className="border-border-grayscale-default rounded-lg px-3 py-2 body1 placeholder:text-text-grayscale-disabled shadow-none h-auto"
              />
            </div>
          </div>
        </div>
      )}

      {/* Contact details */}
      <div className="flex flex-col gap-2">
        <p className="h4 text-text-grayscale-subtitle">Contact details</p>
        <div className="flex flex-col gap-3 p-3 bg-surface-grayscale-subtle rounded-xl shadow-[-2px_-2px_9.5px_rgba(0,0,0,0.04),4px_4px_9.5px_rgba(0,0,0,0.06)]">
          <div className="flex flex-col gap-1">
            <label
              htmlFor="name"
              className="mini2 text-text-grayscale-caption"
            >
              Name*
            </label>
            <Input
              id="name"
              name="name"
              placeholder="Enter your name"
              required
              className="border-border-grayscale-default rounded-lg px-3 py-2 body1 placeholder:text-text-grayscale-disabled shadow-none h-auto"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label
              htmlFor="email"
              className="mini2 text-text-grayscale-caption"
            >
              Email address*
            </label>
            <Input
              id="email"
              name="email"
              type="email"
              placeholder="you@example.com"
              required
              className="border-border-grayscale-default rounded-lg px-3 py-2 body1 placeholder:text-text-grayscale-disabled shadow-none h-auto"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label
              htmlFor="message"
              className="mini2 text-text-grayscale-caption"
            >
              Message
            </label>
            <Textarea
              id="message"
              name="message"
              placeholder="Tell us how we can assist you"
              rows={5}
              className="border-border-grayscale-default rounded-lg px-3 py-2 body1 placeholder:text-text-grayscale-disabled shadow-none field-sizing-fixed resize-none"
            />
          </div>
        </div>
      </div>

      {/* Error message */}
      {submitStatus.status === "error" && (
        <p className="body2 text-red-600">{submitStatus.message}</p>
      )}

      {/* Submit */}
      <Button
        type="submit"
        disabled={isSubmitting}
        className="w-full bg-[#476be1] hover:bg-[#3b5bcc] rounded-lg body1 font-medium h-10"
      >
        {isSubmitting ? "Sending..." : "Send Message"}
      </Button>
    </form>
  );
}
